import { dequal } from "dequal/lite"
import type { StringifyOptions } from "query-string"
import queryString from "query-string"
import { useEffect, useMemo } from "react"
import { useLocation, useNavigate } from "react-router"
import { difference } from "src/common/difference"

type URLSettingsObj = Record<string, any>

const arrayFormat: StringifyOptions["arrayFormat"] = "bracket-separator"

export const useUrlSettings = <T extends URLSettingsObj>(
	currentSettings: T,
	defaultSettings: T,
	onChange: (val: T) => void,
	validate?: (val: T) => { success: boolean; value: T },
) => {
	const navigate = useNavigate()
	const { search } = useLocation()

	const stringifiedSettings = useMemo(() => JSON.stringify(currentSettings), [currentSettings])

	// Settings changed, update URL Search query
	useEffect(() => {
		const settings = JSON.parse(stringifiedSettings)
		const diff = difference(settings, defaultSettings)
		const stringified = queryString.stringify(diff, { arrayFormat })
		if (search && dequal(settings, defaultSettings)) {
			// Remove existing search query
			navigate({ search: "" }, { preventScrollReset: true })
		} else if (`?${stringified}` !== search && !dequal(settings, defaultSettings)) {
			navigate({ search: stringified }, { preventScrollReset: true })
		}
		// Make sure that this is not triggered by search change to prevent infinite loops
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [stringifiedSettings, navigate])

	// URL Search Query changed, update Settings
	useEffect(() => {
		const searchSettings = queryString.parse(search, { arrayFormat })
		const newSettings = {
			...defaultSettings,
			...searchSettings,
		}
		const { success, value } = validate ? validate(newSettings) : { success: true, value: newSettings }
		if (success && !dequal(currentSettings, value)) {
			onChange(value)
		}
		// Make sure that this is only triggered by a search change to prevent infinite loops
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [search])
}
